import React, { useState, useEffect } from 'react';
import api from '../services/api';
import SearchableSelect from './SearchableSelect';
import { useToast } from '../context/ToastContext';

interface SchoolClass {
  id: number;
  name: string;
}

interface Parent {
  id: number;
  name: string;
}

interface Staff {
  id: number;
  name: string;
}

export interface Meeting {
  id?: number;
  title: string;
  start_time: string;
  end_time: string;
  description: string;
  target_classes: number[];
  invited_parents: number[];
  invited_parents_count?: number;
  responsible: number;
  responsible_name?: string;
  status: string;
  minutes?: string;
  revisions?: any[];
}

interface MeetingFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  meetingToEdit?: Meeting | null;
}

const MeetingForm: React.FC<MeetingFormProps> = ({ onSuccess, onCancel, meetingToEdit }) => {
  const { addToast } = useToast();
  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [description, setDescription] = useState('');
  const [selectedClasses, setSelectedClasses] = useState<number[]>([]);
  const [selectedParents, setSelectedParents] = useState<number[]>([]);
  const [responsible, setResponsible] = useState<number | ''>('');
  const [status, setStatus] = useState('Pending');
  
  const [allClasses, setAllClasses] = useState<SchoolClass[]>([]);
  const [allParents, setAllParents] = useState<Parent[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classesRes, staffRes, parentsRes] = await Promise.all([
          api.get('classes/?active_only=true'),
          api.get('staff/?active_only=true'),
          api.get('parents/?active_only=true')
        ]);
        setAllClasses(classesRes.data);
        setAllStaff(staffRes.data);
        setAllParents(parentsRes.data);
      } catch (error) {
        console.error('Failed to fetch data', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (meetingToEdit) {
      setTitle(meetingToEdit.title);
      setStartTime(new Date(meetingToEdit.start_time).toISOString().slice(0, 16));
      setEndTime(new Date(meetingToEdit.end_time).toISOString().slice(0, 16));
      setDescription(meetingToEdit.description);
      setSelectedClasses(meetingToEdit.target_classes || []);
      setSelectedParents(meetingToEdit.invited_parents || []);
      setResponsible(meetingToEdit.responsible || '');
      // Sincroniza status vindo do backend, garantindo fallback para Pending
      const validStatus = ['Pending', 'Completed', 'Canceled', 'Postponed'].includes(meetingToEdit.status) 
        ? meetingToEdit.status 
        : 'Pending';
      setStatus(validStatus);
    } else {
      setTitle('');
      setStartTime('');
      setEndTime('');
      setDescription('');
      setSelectedClasses([]);
      setSelectedParents([]);
      setResponsible('');
      setStatus('Pending');
    }
  }, [meetingToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação de Datas
    if (new Date(endTime) <= new Date(startTime)) {
      addToast('A hora de término deve ser posterior à hora de início.', 'warning');
      return;
    }

    setLoading(true);
    const meetingData = {
      title,
      start_time: startTime,
      end_time: endTime,
      description,
      target_classes: selectedClasses,
      invited_parents_ids: selectedParents,
      responsible,
      status
    };

    try {
      if (meetingToEdit && meetingToEdit.id) {
        await api.patch(`meetings/${meetingToEdit.id}/`, meetingData);
        addToast('Reunião atualizada com sucesso!', 'success');
      } else {
        await api.post('meetings/', meetingData);
        addToast('Nova reunião agendada!', 'success');
      }
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save meeting', error);
      const msg = error.response?.data?.error || 'Erro ao agendar reunião. Verifique os dados.';
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const SectionTitle = ({ title, icon }: { title: string, icon: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', marginTop: '10px' }}>
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <h4 style={{ margin: 0, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--primary-blue)' }}>{title}</h4>
    </div>
  );

  return (
    <div className="card" style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ marginBottom: '25px', borderBottom: '1px solid var(--border-color)', paddingBottom: '15px' }}>
        <h3 style={{ margin: 0 }}>{meetingToEdit ? '🤝 Editar Reunião' : '📅 Agendar Nova Reunião'}</h3>
      </div>

      <form onSubmit={handleSubmit}>
        <SectionTitle title="Assunto e Pauta" icon="💬" />
        <div className="form-group">
          <label>Título da Reunião</label>
          <input 
            type="text" 
            value={title} 
            onChange={(e) => setTitle(e.target.value)} 
            placeholder="Ex: Alinhamento Pedagógico - 1º Trimestre"
            required 
          />
        </div>

        <div className="form-group">
          <label>Descrição / Pauta</label>
          <textarea 
            value={description} 
            onChange={(e) => setDescription(e.target.value)} 
            placeholder="Principais pontos a serem discutidos..."
            style={{ height: '80px' }}
          />
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Horário e Responsável" icon="⏰" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          <div className="form-group">
            <label>Início</label>
            <input type="datetime-local" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Término</label>
            <input type="datetime-local" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} required>
              <option value="Pending">Pendente / Agendada</option>
              <option value="Completed">Concluída</option>
              <option value="Canceled">Cancelada</option>
              <option value="Postponed">Adiada</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Colaborador Responsável</label>
          <select value={responsible} onChange={(e) => setResponsible(Number(e.target.value))} required>
            <option value="">Selecione quem conduzirá a reunião...</option>
            {allStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div style={{ marginTop: '30px' }}>
          <SectionTitle title="Público-Alvo e Convidados" icon="👥" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '25px' }}>
          <SearchableSelect 
            label="Turmas Envolvidas"
            options={allClasses}
            value={selectedClasses}
            onChange={setSelectedClasses}
            multiple
            placeholder="Filtre as turmas..."
          />
          <SearchableSelect 
            label="Pais Convidados (Opcional)"
            options={allParents}
            value={selectedParents}
            onChange={setSelectedParents}
            multiple
            placeholder="Busque pais específicos..."
          />
        </div>

        <div style={{ 
          marginTop: '40px', 
          paddingTop: '20px', 
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button type="button" onClick={onCancel} className="btn btn-secondary">Cancelar</button>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? 'Salvando...' : (meetingToEdit ? 'Atualizar Reunião' : 'Agendar Reunião')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MeetingForm;
